<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import ConfirmDialog from '@/components/editing/ConfirmDialog.vue';

const props = withDefaults(defineProps<{
    open: boolean;
    title: string;
    description?: string;
    dirty?: boolean;
    saving?: boolean;
    saveLabel?: string;
    savingLabel?: string;
    size?: 'default' | 'wide';
    saveDisabled?: boolean;
    requireDirtyToSave?: boolean;
}>(), {
    description: '',
    dirty: false,
    saving: false,
    saveLabel: 'Save changes',
    savingLabel: 'Saving…',
    size: 'default',
    saveDisabled: false,
    requireDirtyToSave: true,
});

const emit = defineEmits<{
    'update:open': [value: boolean];
    save: [];
    cancel: [];
}>();

const discardDialogOpen = ref(false);

function closeSheet() {
    emit('cancel');
    emit('update:open', false);
}

function requestClose() {
    if (props.dirty) {
        discardDialogOpen.value = true;
        return;
    }

    closeSheet();
}

function handleOpenChange(value: boolean) {
    if (!value) requestClose();
    else emit('update:open', true);
}

function discardChanges() {
    discardDialogOpen.value = false;
    closeSheet();
}
</script>

<template>
    <Sheet :open="open" @update:open="handleOpenChange">
        <SheetContent
            side="right"
            :class="[
                '!w-full gap-0 overflow-hidden p-0',
                size === 'wide' ? 'sm:!max-w-[800px]' : 'sm:!max-w-xl',
            ]"
        >
            <SheetHeader class="shrink-0 border-b border-border/60 px-6 py-5 pr-14 text-left">
                <SheetTitle class="text-xl font-semibold tracking-tight">{{ title }}</SheetTitle>
                <SheetDescription v-if="description" class="leading-6">{{ description }}</SheetDescription>
            </SheetHeader>
            <div class="min-h-0 flex-1 overflow-y-auto px-6 py-5"><slot /></div>
            <SheetFooter class="sheet-safe-footer shrink-0 border-t border-border/60 bg-background/95 px-6 py-4 backdrop-blur sm:flex-row sm:justify-end">
                <Button variant="outline" :disabled="saving" @click="requestClose">Cancel</Button>
                <Button :disabled="saving || (requireDirtyToSave && !dirty) || saveDisabled" @click="emit('save')">{{ saving ? savingLabel : saveLabel }}</Button>
            </SheetFooter>
        </SheetContent>
    </Sheet>

    <ConfirmDialog
        v-model:open="discardDialogOpen"
        title="Discard unsaved changes?"
        description="Your changes have not been saved. Discard them and close this sheet?"
        confirm-label="Discard changes"
        destructive
        @confirm="discardChanges"
    />
</template>
